import { Button, Input } from "@mantine/core";
import {
  IconBan,
  IconBriefcase,
  IconBuilding,
  IconFileText,
  IconHash,
  IconId,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPhoneCall,
  IconPlus,
  IconPrinter,
  IconTrash,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface PartnerContact {
  name: string;
  department: string;
  telephone: string;
  email: string;
}

function EditCoopPage() {
  const { t } = useTranslation();
  const [coopData, setCoopData] = useState<any>(null);
  const [editCoopData, setEditCoopData] = useState<any>({});
  const [lockedStatus, setLockedStatus] = useState<
    "underReview" | "disabled" | null
  >(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    name = "",
    abbreviation = "",
    serialNumber = "",
    businessIdNumber = "",
    invoiceType = "",
    businessAddress = "",
    invoiceAddress = "",
    shippingAddress = "",
    telephone = "",
    fax = "",
    contacts = [] as PartnerContact[],
    business = "",
    status = "",
  } = coopData || {};

  const handleChangeData = (value: string, type: string) => {
    setCoopData({
      ...coopData,
      [type]: value,
    });

    setEditCoopData((prevData: any) => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleChangeAddressData = (
    value: string,
    addressType: string,
    type: string
  ) => {
    const processedValue =
      type === "postalCode" ? Number(value) || 0 : value;
    setCoopData((prevData: any) => ({
      ...prevData,
      [addressType]: {
        ...prevData[addressType],
        [type]: processedValue,
      },
    }));

    setEditCoopData((prevData: any) => ({
      ...prevData,
      [addressType]: {
        city: "",
        district: "",
        postalCode: 0,
        address: "",
        ...(coopData?.[addressType] ?? {}),
        ...prevData[addressType],
        [type]: processedValue,
      },
    }));
  };

  const handleAddContact = () => {
    const newContact: PartnerContact = {
      name: "",
      department: "",
      telephone: "",
      email: "",
    };
    const nextContacts = [...(contacts as PartnerContact[]), newContact];
    setCoopData((prevData: any) => ({ ...prevData, contacts: nextContacts }));
    setEditCoopData((prevData: any) => ({
      ...prevData,
      contacts: nextContacts,
    }));
  };

  const handleRemoveContact = (index: number) => {
    const nextContacts = (contacts as PartnerContact[]).filter(
      (_, i) => i !== index
    );
    setCoopData((prevData: any) => ({ ...prevData, contacts: nextContacts }));
    setEditCoopData((prevData: any) => ({
      ...prevData,
      contacts: nextContacts,
    }));
  };

  const handleChangeContactField = (
    index: number,
    field: keyof PartnerContact,
    value: string
  ) => {
    const nextContacts = (contacts as PartnerContact[]).map((contact, i) =>
      i === index ? { ...contact, [field]: value } : contact
    );
    setCoopData((prevData: any) => ({ ...prevData, contacts: nextContacts }));
    setEditCoopData((prevData: any) => ({
      ...prevData,
      contacts: nextContacts,
    }));
  };

  const handleSubmitEdit = async ({ id }: { id: string }) => {
    if (lockedStatus) return;
    try {
      await single(
        orpcClient.partner.update([
          {
            id: id!,
            ...editCoopData,
          },
        ])
      );

      navigate("/hrMainPage/coop");
    } catch (error) {
      console.error("partner.update_ERROR", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        let result = await single(orpcClient.partner.read([{ id: id! }]));
        if (result?.status === "underReview" || result?.status === "disabled") {
          setLockedStatus(result.status);
        }
        setCoopData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchData();
  }, [id]);

  const renderControl = (
    label: string,
    control: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {control}
    </div>
  );

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex min-h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
        {value || value === 0 ? value : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  const renderAddressSection = (
    title: string,
    addressType: string,
    addr: any
  ) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <IconMapPin size={16} className="text-blue-600" />
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
        {renderControl(
          t("coop.city") || "縣市",
          <Input
            value={addr?.city || ""}
            onChange={(e) =>
              handleChangeAddressData(e.target.value, addressType, "city")
            }
            leftSection={<IconMapPin size={16} />}
          />,
          IconMapPin
        )}
        {renderControl(
          t("coop.district") || "行政區",
          <Input
            value={addr?.district || ""}
            onChange={(e) =>
              handleChangeAddressData(e.target.value, addressType, "district")
            }
            leftSection={<IconMapPin size={16} />}
          />,
          IconMapPin
        )}
        {renderControl(
          t("coop.postalCode") || "郵遞區號",
          <Input
            value={addr?.postalCode ?? ""}
            onChange={(e) =>
              handleChangeAddressData(e.target.value, addressType, "postalCode")
            }
            leftSection={<IconHash size={16} />}
          />,
          IconHash
        )}
        {renderControl(
          t("coop.address") || "地址",
          <Input
            value={addr?.address || ""}
            onChange={(e) =>
              handleChangeAddressData(e.target.value, addressType, "address")
            }
            leftSection={<IconMapPin size={16} />}
          />,
          IconMapPin
        )}
      </div>
    </div>
  );

  if (lockedStatus) {
    return (
      <div className="min-h-screen p-8">
        <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {name || t("coop.editLockedTitle") || "無法編輯合作方"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("coop.editLockedTitle") || "無法編輯合作方"}
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
              <IconBan size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {lockedStatus === "underReview"
                  ? t("coop.pending") || "審核中"
                  : t("coop.inactive") || "停用"}
              </h3>
            </div>
            <div className="p-6 text-sm text-gray-500">
              {lockedStatus === "underReview"
                ? t("coop.editLockedPending") ||
                  "此合作方正在停用審核中，無法編輯資料。"
                : t("coop.editLockedDisabled") || "此合作方已停用，無法編輯資料。"}
            </div>
          </div>

          <Button
            variant="default"
            style={{ width: "100%" }}
            radius="md"
            onClick={() => navigate(`/hrMainPage/coop/${id}`)}
          >
            {t("coop.backToDetail") || "回到合作方詳細資訊"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {name || t("coop.editTitle") || "編輯合作方資訊"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("coop.editTitle") || "編輯合作方資訊"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("coop.companyName") || "公司名",
              <Input
                value={name}
                onChange={(e) => handleChangeData(e.target.value, "name")}
                leftSection={<IconBuilding size={16} />}
              />,
              IconBuilding
            )}
            {renderControl(
              t("coop.abbreviation") || "簡稱",
              <Input
                value={abbreviation}
                onChange={(e) =>
                  handleChangeData(e.target.value, "abbreviation")
                }
                leftSection={<IconBuilding size={16} />}
              />,
              IconBuilding
            )}
            {renderControl(
              t("coop.businessIdNumber") || "統編",
              <Input
                value={businessIdNumber}
                onChange={(e) =>
                  handleChangeData(e.target.value, "businessIdNumber")
                }
                leftSection={<IconId size={16} />}
              />,
              IconId
            )}
            {renderControl(
              "廣達組織 ID",
              <Input
                value={serialNumber}
                onChange={(e) =>
                  handleChangeData(e.target.value, "serialNumber")
                }
                leftSection={<IconHash size={16} />}
              />,
              IconHash
            )}
          </div>
        </div>

        {renderAddressSection(
          t("coop.businessRegisterAddress") || "1. 營業登記地址",
          "businessAddress",
          businessAddress
        )}
        {renderAddressSection(
          t("coop.invoiceAddressSection") || "2. 發票地址",
          "invoiceAddress",
          invoiceAddress
        )}
        {renderAddressSection(
          t("coop.shippingAddressSection") || "3. 送貨地址",
          "shippingAddress",
          shippingAddress
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPhoneCall size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.contactInfo") || "聯絡資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("coop.phone") || "電話",
              <Input
                value={telephone}
                onChange={(e) => handleChangeData(e.target.value, "telephone")}
                leftSection={<IconPhone size={16} />}
              />,
              IconPhone
            )}
            {renderControl(
              t("coop.fax") || "傳真",
              <Input
                value={fax}
                onChange={(e) => handleChangeData(e.target.value, "fax")}
                leftSection={<IconPrinter size={16} />}
              />,
              IconPrinter
            )}
            {renderControl(
              t("coop.invoiceType") || "發票種類",
              <Input
                value={invoiceType}
                onChange={(e) => handleChangeData(e.target.value, "invoiceType")}
                leftSection={<IconFileText size={16} />}
              />,
              IconFileText
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconUsers size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("coop.contactPersonInfo") || "聯絡人資訊"}
              </h3>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddContact}
            >
              {t("coop.addContact") || "新增聯絡人"}
            </Button>
          </div>
          <div className="space-y-4 p-6">
            {(contacts as PartnerContact[]).length === 0 ? (
              <div className="text-sm text-gray-400">
                {t("coop.noContacts") || "尚未新增聯絡人"}
              </div>
            ) : (
              (contacts as PartnerContact[]).map((contact, index) => (
                <div key={index} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div
                      className="text-sm font-medium"
                      style={{ color: "#6F6F6F" }}
                    >
                      {t("coop.contactNumber", { number: index + 1 }) ||
                        `聯絡人 #${index + 1}`}
                    </div>
                    <Button
                      variant="subtle"
                      color="red"
                      size="sm"
                      radius="md"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => handleRemoveContact(index)}
                    >
                      {t("coop.removeContact") || "移除聯絡人"}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {renderControl(
                      t("coop.contactPerson") || "聯絡人",
                      <Input
                        value={contact.name}
                        onChange={(e) =>
                          handleChangeContactField(index, "name", e.target.value)
                        }
                        leftSection={<IconUser size={16} />}
                      />,
                      IconUser
                    )}
                    {renderControl(
                      t("coop.department") || "部門",
                      <Input
                        value={contact.department}
                        onChange={(e) =>
                          handleChangeContactField(
                            index,
                            "department",
                            e.target.value
                          )
                        }
                        leftSection={<IconBuilding size={16} />}
                      />,
                      IconBuilding
                    )}
                    {renderControl(
                      t("coop.phone") || "電話",
                      <Input
                        value={contact.telephone}
                        onChange={(e) =>
                          handleChangeContactField(
                            index,
                            "telephone",
                            e.target.value
                          )
                        }
                        leftSection={<IconPhone size={16} />}
                      />,
                      IconPhone
                    )}
                    {renderControl(
                      t("coop.email") || "E-mail",
                      <Input
                        value={contact.email}
                        onChange={(e) =>
                          handleChangeContactField(index, "email", e.target.value)
                        }
                        leftSection={<IconMail size={16} />}
                      />,
                      IconMail
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBriefcase size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.businessType") || "營業項目"}
            </h3>
          </div>
          <div className="p-6">
            {renderControl(
              t("coop.businessType") || "營業項目",
              <Input
                value={business}
                onChange={(e) => handleChangeData(e.target.value, "business")}
                leftSection={<IconBriefcase size={16} />}
              />,
              IconBriefcase
            )}
          </div>
        </div>

        {status === "underReview" ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
              <IconBan size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">停用申請</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
              {renderField("申請人", coopData?.applicant?.name, IconUser)}
              {renderField("申請時間", coopData?.applicationDate, IconFileText)}
              {renderField("審核人", coopData?.reviewer?.name, IconUser)}
              {renderField("審核時間", coopData?.reviewDate, IconFileText)}
              {renderField("審核狀態", "審核中", IconFileText)}
              {renderField("停用原因", coopData?.disableReason, IconFileText)}
            </div>
          </div>
        ) : null}

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => {
              navigate(`/hrMainPage/coop/${id}`);
            }}
          >
            {t("coop.cancel") || "取消修改"}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmitEdit({ id: id! })}
          >
            {t("coop.confirm") || "確認修改"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditCoopPage;
